import { enrichCrashDetail } from './merge-constants'
import { buildMatchGridRows } from './match-grid'

/**
 * @param {unknown} collection
 * @returns {unknown[]}
 */
function asArray(collection) {
  if (collection == null) return []
  if (Array.isArray(collection)) return collection
  if (typeof collection === 'object') return Object.values(collection)
  return []
}

/**
 * @param {Record<string, unknown>} crash
 * @returns {Array<Record<string, unknown> & { unit?: Record<string, unknown>, unitIndex?: number, personIndex?: number }>}
 */
export function collectPeopleFromCrash(crash) {
  if (!crash || typeof crash !== 'object') return []

  const rootPeople = asArray(crash.people).map((person, personIndex) => ({
    ...person,
    personIndex,
  }))

  const units = asArray(crash.units)
  if (units.length > 0) {
    const fromUnits = units.flatMap((unit, unitIndex) =>
      asArray(unit?.people).map((person, personIndex) => ({
        ...person,
        unit,
        unitIndex,
        personIndex,
      })),
    )
    return [...rootPeople, ...fromUnits]
  }

  if (crash.unit && typeof crash.unit === 'object') {
    return asArray(crash.unit.people).map((person, personIndex) => ({
      ...person,
      unit: crash.unit,
      unitIndex: 0,
      personIndex,
    }))
  }

  return rootPeople
}

/**
 * @param {Record<string, unknown>} person
 * @returns {{ name: string, surname: string }}
 */
export function getPersonNameAndSurname(person) {
  if (!person || typeof person !== 'object') {
    return { name: '', surname: '' }
  }
  const name =
    person.Name ??
    person.name ??
    person['First name'] ??
    person['Given name'] ??
    ''
  const surname =
    person.Surname ??
    person.surname ??
    person['Last name'] ??
    person['Family name'] ??
    ''
  return { name: String(name ?? ''), surname: String(surname ?? '') }
}

/**
 * @param {Record<string, unknown>} person
 * @returns {string}
 */
export function getPersonKey(person) {
  const personId = person?.['Person id']
  if (personId !== undefined && personId !== null && personId !== '') {
    return `pid:${personId}`
  }
  return `u${person?.unitIndex ?? 0}-p${person?.personIndex ?? 0}`
}

/**
 * @param {Record<string, unknown>} person
 * @param {Record<string, unknown>} matchMeta
 * @returns {boolean}
 */
function matchesCandidateAgainstMeta(person, matchMeta) {
  const candName = String(matchMeta?.candidate_name ?? '').trim().toLowerCase()
  const candSurname = String(matchMeta?.candidate_surname ?? '').trim().toLowerCase()
  if (!candName && !candSurname) return false

  const personName = String(person?.Name ?? '').trim().toLowerCase()
  const personSurname = String(person?.Surname ?? '').trim().toLowerCase()
  return personName === candName && personSurname === candSurname
}

/**
 * @param {Record<string, unknown>} person
 * @param {Record<string, unknown> | Record<string, unknown>[]} matchMeta
 * @returns {boolean}
 */
export function matchesCandidate(person, matchMeta) {
  const metas = Array.isArray(matchMeta)
    ? matchMeta
    : (matchMeta?._matchMetas ?? [matchMeta])
  return metas.some((meta) => matchesCandidateAgainstMeta(person, meta))
}

/**
 * @deprecated Usare buildMatchGridRows(rawCrash, configuration, options)
 */
export function buildCrashDisplayRows(rawCrash, _configOptions, options = {}) {
  return buildMatchGridRows(rawCrash, options.configuration ?? {}, options)
}

/**
 * Normalizza un match selezionato per il merge: tutte le persone + flag candidato/inclusione.
 * @param {Record<string, unknown>} [myMatch]
 * @returns {Record<string, unknown> | null}
 */
export function prepareMatchForMerge(myMatch) {
  if (!myMatch) return null
  const enriched = enrichCrashDetail(myMatch)
  const people = collectPeopleFromCrash(myMatch).map((person) => {
    const { unit, ...personOnly } = person
    const isCandidate = matchesCandidate(personOnly, myMatch)
    return {
      ...personOnly,
      isMatchCandidate: isCandidate,
      includeInMerge: isCandidate,
    }
  })

  return {
    ...enriched,
    people,
    units: enriched?.units?.map((unit, unitIndex) => ({
      ...unit,
      people: [],
    })),
    ...(myMatch._matchMetas ? { _matchMetas: myMatch._matchMetas } : {}),
    ...(myMatch.candidate_name ? { candidate_name: myMatch.candidate_name } : {}),
    ...(myMatch.candidate_surname ? { candidate_surname: myMatch.candidate_surname } : {}),
  }
}

/**
 * @param {Record<string, unknown>} person
 * @returns {string}
 */
export function formatPersonDisplayName(person) {
  const name = [person?.Name, person?.Surname].filter(Boolean).join(' ').trim()
  if (name) return name
  const personId = person?.['Person id']
  return personId != null && personId !== '' ? `Person ${personId}` : '—'
}

/**
 * Persone candidate al merge incrociato tra organizzazioni.
 * @param {Record<string, unknown>[] | undefined} people
 * @returns {Record<string, unknown>[]}
 */
export function getPeopleToMerge(people) {
  if (!Array.isArray(people)) return []
  return people.filter(
    (person) => person?.isMatchCandidate === true && person?.includeInMerge !== false,
  )
}

/**
 * Persone incluse nel crash finale ma senza form di merge.
 * @param {Record<string, unknown>[] | undefined} people
 * @returns {Record<string, unknown>[]}
 */
export function getPeopleCarriedWithoutMerge(people) {
  if (!Array.isArray(people)) return []
  return people.filter(
    (person) => person?.includeInMerge === true && person?.isMatchCandidate !== true,
  )
}

/**
 * @param {Record<string, unknown>} a
 * @param {Record<string, unknown>} b
 * @returns {boolean}
 */
export function matchesPersonIdentity(a, b) {
  const nameA = String(a?.Name ?? '').trim().toLowerCase()
  const nameB = String(b?.Name ?? '').trim().toLowerCase()
  const surnameA = String(a?.Surname ?? '').trim().toLowerCase()
  const surnameB = String(b?.Surname ?? '').trim().toLowerCase()
  return nameA === nameB && surnameA === surnameB && (nameA !== '' || surnameA !== '')
}

/**
 * Persone da validare/mergiare nel wizard (un form per elemento restituito).
 * - Candidati espliciti sull'anchor o sui match → solo quelli (merge incrociato).
 * - Match cross-org con candidati → una persona anchor abbinata al candidato primario.
 * - Validazione-only / nessun candidato → tutte le persone incluse sull'anchor.
 * @param {Record<string, unknown>[] | undefined} anchorPeople
 * @param {...(Record<string, unknown> | null | undefined)} matchDetails
 * @returns {Record<string, unknown>[]}
 */
export function resolvePeopleToMerge(anchorPeople, ...matchDetails) {
  const anchor = anchorPeople ?? []
  const flaggedOnAnchor = getPeopleToMerge(anchor)
  if (flaggedOnAnchor.length > 0) return flaggedOnAnchor

  const matchCandidates = matchDetails.flatMap((detail) =>
    getPeopleToMerge(detail?.people ?? []),
  )
  if (matchCandidates.length > 0) {
    const primaryCandidate = matchCandidates[0]
    const matchedAnchor = anchor.find((person) =>
      matchesPersonIdentity(person, primaryCandidate),
    )
    if (matchedAnchor) return [matchedAnchor]
    if (anchor.length > 0) return [anchor[0]]
    return [primaryCandidate]
  }

  const included = getPeopleIncludedInMerge(anchor)
  return included
}

/**
 * @param {Array<{ detail?: Record<string, unknown> | null, roleLabel?: string, role?: string }>} sources
 * @returns {Array<Record<string, unknown> & { carryRoleLabel?: string, carryRole?: string }>}
 */
export function collectCarriedPeopleFromMatchDetails(sources = []) {
  return sources.flatMap(({ detail, roleLabel, role }) =>
    getPeopleCarriedWithoutMerge(detail?.people ?? []).map((person) => ({
      ...person,
      carryRoleLabel: roleLabel,
      carryRole: role,
    })),
  )
}

/**
 * @param {Record<string, unknown>[] | undefined} people
 * @returns {Record<string, unknown>[]}
 */
export function getPeopleIncludedInMerge(people) {
  if (!Array.isArray(people)) return []
  return people.filter((person) => person?.includeInMerge !== false)
}

/**
 * @param {Record<string, unknown> | null | undefined} detail
 * @returns {Record<string, unknown>[]}
 */
function asArrayUnits(detail) {
  return asArray(detail?.units)
}

/**
 * Unità da validare nel merge: anchor se presente, altrimenti il massimo tra i ruoli matchati.
 * @param {Record<string, unknown> | null | undefined} anchorDetail
 * @param {Record<string, { units?: unknown } | null | undefined>} crashByRole
 * @returns {Array<Record<string, unknown> & { done?: boolean }>}
 */
export function buildMergeUnitSlots(anchorDetail, crashByRole = {}) {
  const anchorUnits = asArrayUnits(anchorDetail)
  const roleUnits = ['police', 'hospital', 'insurance'].map((role) =>
    asArrayUnits(crashByRole?.[role]),
  )
  const slotCount = Math.max(anchorUnits.length, ...roleUnits.map((units) => units.length), 0)
  if (slotCount === 0) return []

  return Array.from({ length: slotCount }, (_, index) => {
    const existing = anchorUnits[index]
    if (existing && Object.keys(existing).length > 0) {
      const { people, ...unitOnly } = existing
      return { ...unitOnly, done: existing.done === true }
    }

    const template =
      roleUnits.map((units) => units[index]).find((unit) => unit && typeof unit === 'object') ??
      {}
    const { people, ...unitOnly } = /** @type {Record<string, unknown>} */ (template)
    return { ...unitOnly, done: false }
  })
}

/**
 * @param {Record<string, unknown> | null | undefined} unit
 * @returns {Record<string, unknown>}
 */
export function stripUnitPeople(unit) {
  if (!unit || typeof unit !== 'object') return {}
  const { people, ...unitOnly } = unit
  return unitOnly
}

/**
 * @param {Record<string, unknown>} detail
 * @param {string} personKey
 * @param {boolean} included
 * @returns {Record<string, unknown>}
 */
export function setPersonIncludeInMerge(detail, personKey, included) {
  if (!detail || !personKey) return detail
  return {
    ...detail,
    people: (detail.people ?? []).map((person) =>
      getPersonKey(person) === personKey
        ? { ...person, includeInMerge: included }
        : person,
    ),
  }
}
